/* Built In Imports */
import { useRouter } from 'next/router';
import Script from 'next/script';
import { Fragment, useEffect, useState } from 'react';

/* External Imports */
import { Box, Divider, Flex, Heading, Text } from '@chakra-ui/react';
import parser from 'html-react-parser';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';

/* Internal Imports */
/* Components */
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import SubscribeContent from '@components/UI/Subscribe/SubscribeContent';
import { ShortCodeProcess } from '@components/Utility/ArticleParser/ShortCode';
import SocialIcon from '@components/Utility/SocialIcon';
import { setAudioPlayer } from '@components/Utility/utils';
import config from '@config';
import { LangStyle } from 'constants/index';
// import LikeBookmark from '@components/Utility/LikeBookmark';

/* Services */
/* Styles */
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

/**
 * Renders the Article component
 *
 * @param {object} articleData - data
 * @param {string} language - Language of the page
 * @param {boolean} isLikeBookmarkVisible
 * @param {Array} updateCards
 * @returns {ReactElement} Article component
 */

const Article = ({
  articleData,
  language,
  isLikeBookmarkVisible,
  updateCards,
  type,
}) => {
  const router = useRouter();
  const { client } = router.query;
  const [showBreadCrumb, setShowBreadCrumb] = useState(true);
  const [isAudioLoad, setAudioLoad] = useState(false);
  const [youtubeID, setYoutubeID] = useState('');
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  const date = new Date(articleData?.createdAt).toLocaleDateString(
    'en-US',
    options
  );

  const handleAudioClick = () => {
    if (!isAudioLoad) {
      setAudioLoad(true);
    }
  };

  // const langFont = {
  //   hi: 'Mukta',
  //   ta: 'MuktaMalar',
  // };

  let langClass = '';
  if (language === 'ta' || language === 'hi') {
    langClass = 'lang-' + language;
  }
  const articleBody =
    (articleData?.body[0]?.value &&
      ShortCodeProcess(articleData?.body[0]?.value.toString())) ||
    '';

  useEffect(() => {
    if (
      router.asPath.includes('client=sadhguru-app') ||
      client === 'sadhguru-app'
    ) {
      setShowBreadCrumb(false);
    } else {
      setShowBreadCrumb(true);
    }
    if (typeof window !== 'undefined' && type == 'audio' && isAudioLoad) {
      setAudioPlayer(articleData?.mediaUrl[0]?.value[0]?.embedUrl);
    }
  }, [client, router, isAudioLoad]);
  useEffect(() => {
    if (type === 'video') {
      const url = articleData?.mediaUrl[0]?.value[0]?.embedUrl;
      const VID_REGEX =
        /(?:youtube(?:-nocookie)?\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
      const id = url?.match(VID_REGEX)[1];
      setYoutubeID(id);
    }
  });

  return (
    <Fragment>
      <Box as="section" float="left" width="100%" textAlign="left">
        <Box
          //   background={`url(/_next/static/media/wisdom-article.816bb625e6a548c7d20e00ddf894acfc.png)`}
          //   backgroundSize="cover"
          //   backgroundPosition="center"
          //   backgroundRepeat="no-repeat"

          float="left"
          width="100%"
          textAlign="center"
          pb="20px"
          height="auto"
        >
          {type !== 'poem' && (
            <Box background="#28231e" minHeight="170px">
              <Heading
                as="h1"
                display="inline-block"
                width="100%"
                maxWidth="850px"
                textAlign="center"
                fontFamily={
                  LangStyle[language]?.fontFamily
                    ? LangStyle[language].fontFamily
                    : 'FedraSerifAStdBook'
                }
                fontSize={{ base: '27px', md: '45px', lg: '45px' }}
                color="#faf7f0"
                margin="0"
                lineHeight="1.11"
                p={{
                  base: '19px 16px',
                  md: '27px 56px 16px',
                  lg: '60px 5px 0',
                }}
                fontWeight="500"
              >
                {articleData?.title[0]?.value}
                <Text
                  fontSize={{ base: '16px', md: '18px', lg: '20px' }}
                  color="#aea493"
                  padding={{
                    base: '7px 40px 44px 40px',
                    md: '7px 40px 0px 40px',
                    lg: '7px 40px 0px 40px',
                  }}
                  textAlign="center"
                  fontFamily="FedraSansStd-book-i"
                  lineHeight={{ base: '1.5', lg: '1.7' }}
                >
                  {articleData?.summary[0]?.value}
                </Text>
              </Heading>
            </Box>
          )}
          <Box float="left" width="100%">
            <Box>
              {type == 'poem' && (
                <Box paddingBottom="10px" background="#00334a">
                  {' '}
                  <Box
                    fontFamily="FedraSansStd-book,sans-serif"
                    fontSize={{ base: '16px', md: '18px', lg: '20px' }}
                    p={{
                      base: '0px 30px',
                      sm: '0px 30px',
                      md: '0px 35px',
                      lg: '0px calc((100% - 750px) / 2)',
                    }}
                    color="#dfd6c4"
                    lineHeight="1.7"
                    className="articlebody"
                  >
                    {parser(articleBody)}
                    {articleData?.body?.includes('<script') && ( // Need to keep a watch if script tags are used other than for twitter widget
                      <Script
                        async
                        src="https://platform.twitter.com/widgets.js"
                        charset="utf-8"
                      ></Script>
                    )}
                  </Box>
                  <Box>
                    <LazyLoadImageComponent
                      alt=""
                      src={`${config.imgPath}/d/46272/1663645833-flower-divider-horizontal-new.svg`}
                    />
                  </Box>
                </Box>
              )}
              <Box
                clipPath="polygon(0 0, 100% 0, 100% 80%, 0% 100%)"
                backgroundColor={type == 'poem' ? '#00334a' : '#28231e'}
                width={{ base: '100%' }}
                minHeight={{
                  base: '20px',
                  sm: type !== 'poem' ? '20px' : '130px',
                  md: '130px',
                  // lg: type !== 'poem' ? '350px' : '350px',
                  lg: '120px',
                  xl: '340px',
                }}
                margin="-2px auto 0"
              ></Box>
            </Box>
            <Box
              margin="0 auto"
              width={{
                base: type !== 'poem' ? '100%' : '94%',
                lg: '80%',
                xl: '66%',
              }}
            >
              {/* {articleData.heroImage !== undefined &&
                articleData.heroImage !== null && ( */}
              <Box
                boxShadow={{
                  base: 'rgb(0 0 0 / 100%) 0 0 60px',
                  lg: 'rgb(0 0 0 / 50%) 0 0 60px',
                }}
                maxW={{ base: 'unset', lg: '848px' }}
              >
                <Box
                  as="span"
                  position="relative"
                  mt={{
                    base: '-68px',
                    lg: '-110px',
                    xl: type !== 'poem' ? '-290px' : '-182px',
                  }}
                  display="block"
                >
                  <Box>
                    {type === 'video' ? (
                      articleData?.mediaUrl[0]?.value[0]?.embedUrl && (
                        <Box
                          pos="relative"
                          w={{ base: '100%', md: '96%', lg: '100%' }}
                          m="0 auto 30px"
                          maxH="477px"
                        >
                          <LiteYouTubeEmbed id={youtubeID} cookie={true} />
                        </Box>
                      )
                    ) : (
                      <LazyLoadImageComponent
                        minHeight={{ base: '233px', md: '477px', lg: '540px' }}
                        src={
                          articleData?.heroImage[0]?.value &&
                          articleData?.heroImage[0]?.value?.url
                        }
                        alt={
                          articleData?.heroImage[0]?.value &&
                          articleData?.heroImage[0]?.value?.alt
                        }
                        title={
                          articleData?.heroImage[0]?.value &&
                          articleData?.heroImage[0]?.value?.alt
                        }
                      />
                    )}{' '}
                  </Box>
                  {type == 'audio' &&
                    articleData?.mediaUrl[0]?.value[0]?.embedUrl && (
                      <Box className="audio-player">
                        <Box className="timeline">
                          <Box className="progress"></Box>
                        </Box>
                        <Box className="controls">
                          <Box className="play-container">
                            <Box
                              className="toggle-play play"
                              onClick={handleAudioClick}
                            ></Box>
                          </Box>
                          <Box className="time">
                            <Box className="current"></Box>
                            <Box className="length"></Box>
                          </Box>
                          <Box className="volume-container">
                            <Box className="volume-button">
                              <Box className="volume icono-volumeMedium"></Box>
                            </Box>
                            <Box className="volume-slider">
                              <Box className="volume-percentage"></Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}
                </Box>
                {/* {type !== 'poem' && articleData.heroImage.title && (
                      <Box className={classes['slider-label']}>
                        {articleData.heroImage.title}
                      </Box>
                    )} */}
              </Box>
              {/* )} */}
            </Box>
            {type === 'poem' && (
              <Box p={{ base: '0', sm: '0', md: '25px 0', lg: '25px 0' }}>
                {parser(ShortCodeProcess(articleData?.editorsNote?.toString()))}
              </Box>
            )}
            <Box
              pos="relative"
              maxW="850px"
              m={{ base: '0', sm: '0', lg: '20px auto' }}
            >
              <Flex
                flexDirection={{
                  base: 'column',
                  md: 'row',
                  sm: 'column',
                  lg: 'row',
                }}
                justifyContent="space-between"
                mb="2vw"
              >
                <Box mb="20px" color="#aea493" fontFamily="FedraSansStd-book">
                  <Box textAlign={{ base: 'center', md: 'left' }}>
                    <Text fontSize="14px" textTransform="capitalize">
                      {type}
                    </Text>
                    <Text fontSize="16px">{date}</Text>
                  </Box>
                </Box>
                <Box mb={{ base: '0', sm: '0', lg: '20px' }}>
                  <Divider orientation="vertical" borderColor="#aea493" />
                </Box>
                <Box mb={{ base: '0', sm: '0', lg: '20px' }}>
                  <Box
                    position="relative"
                    float="left"
                    height={{ base: 'auto', md: '0' }}
                    width={{ base: '100%', md: 'auto' }}
                    boxShadow={{
                      base: '0 -6px 10px 0 rgb(0 0 0 / 7%)',
                      md: 'none',
                    }}
                    top="-30%"
                  >
                    <SocialIcon
                      url={articleData?.url}
                      isVisible={true}
                      direction={'row'}
                      position={'relative'}
                      isLikeBookmarkVisible={isLikeBookmarkVisible}
                      isLiked={articleData?.isLiked}
                      isBookmarked={articleData?.isBookmarked}
                      updateCards={updateCards}
                      // Download is available only for audio and video
                      freeSadhguruDownloadsId={
                        articleData?.freeSadhguruDownloadsId[0]?.value
                      }
                    />
                  </Box>
                </Box>
                {/* <Box>
                  <LikeBookmark
                    id={pageData.uuid}
                    isLiked={pageData.isLiked}
                    isBookmarked={pageData.isBookmarked}
                    updateCards={updateCards}
                  />
                </Box> */}
              </Flex>
            </Box>
          </Box>
        </Box>
        {type !== 'poem' && (
          <Box
            fontFamily="FedraSansStd-book,sans-serif"
            fontSize={{ base: '16px', md: '20px', lg: '20px' }}
            p={{
              base: '0px 30px',
              sm: '0px 30px',
              md: '0px 35px',
            }}
            color="#28231e"
            lineHeight="1.7"
            className="articlebody"
          >
            <Box w={{ base: '96%', lg: '66%' }} m="0 auto">
              <Box className="article-content"> {parser(articleBody)} </Box>{' '}
              {articleData?.body?.includes('<script') && ( // Need to keep a watch if script tags are used other than for twitter widget
                <Script
                  async
                  src="https://platform.twitter.com/widgets.js"
                  charset="utf-8"
                ></Script>
              )}
            </Box>
          </Box>
        )}
        <Box w={{ base: '96%', lg: '66%' }} m="0 auto">
          <SubscribeContent
            language={language}
            contentSection="blog"
            contentType={'article'}
          />
        </Box>
      </Box>
    </Fragment>
  );
};

export default Article;
